import React, { useState } from 'react';
import { Star, MessageCircle, CheckCircle } from 'lucide-react';

const BookingAvailability = ({ artist }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    eventType: '',
    expectedAttendees: '',
    budget: '',
    location: '',
    additionalInfo: ''
  });

  // Mock availability data
  const availableDates = [
    { date: '2024-10-15', available: true, price: 5000 },
    { date: '2024-10-18', available: true, price: 5000 },
    { date: '2024-10-22', available: false, price: null },
    { date: '2024-10-25', available: true, price: 6000 },
    { date: '2024-11-01', available: true, price: 5500 },
    { date: '2024-11-05', available: true, price: 5000 },
    { date: '2024-11-12', available: false, price: null },
    { date: '2024-11-20', available: true, price: 7000 }
  ];

  // Mock pricing packages
  const pricingPackages = [
    {
      id: 'basic',
      name: 'Formule Essentielle',
      price: 3000,
      duration: '2 heures',
      description: 'Performance DJ avec matériel de base',
      features: [
        'Set DJ de 2 heures',
        'Matériel audio standard',
        'Éclairage de base',
        'Consultation pre-événement'
      ],
      suitable: 'Parfait pour événements privés et petites fêtes'
    },
    {
      id: 'premium',
      name: 'Formule Premium',
      price: 5000,
      duration: '3-4 heures',
      description: 'Performance complète avec équipement professionnel',
      features: [
        'Set DJ de 3-4 heures',
        'Matériel audio professionnel',
        'Éclairage dynamique',
        'Écrans LED',
        'Consultation et playlist personnalisée',
        'Technicien inclus'
      ],
      suitable: 'Idéal pour mariages et événements corporate',
      popular: true
    },
    {
      id: 'exclusive',
      name: 'Formule Exclusive',
      price: 8000,
      duration: 'Journée complète',
      description: 'Expérience complète avec performance live',
      features: [
        'Performance de 6+ heures',
        'Matériel haut de gamme',
        'Éclairage spectacle complet',
        'Effets spéciaux',
        'Musiciens invités possibles',
        'Équipe technique complète',
        'Consultation approfondie',
        'Enregistrement possible'
      ],
      suitable: 'Pour festivals et grands événements'
    }
  ];

  const eventTypes = [
    { id: 'wedding', label: 'Mariage', icon: '💒' },
    { id: 'corporate', label: 'Événement corporate', icon: '🏢' },
    { id: 'private', label: 'Fête privée', icon: '🎉' },
    { id: 'festival', label: 'Festival', icon: '🎪' },
    { id: 'concert', label: 'Concert', icon: '🎵' },
    { id: 'other', label: 'Autre', icon: '🎤' }
  ];

  const handleBookingSubmit = (e) => {
    e?.preventDefault();
    // Handle booking submission
    console.log('Booking submitted:', {
      date: selectedDate,
      package: selectedPackage,
      form: bookingForm
    });
    alert('Demande de réservation envoyée ! Vous recevrez une confirmation par email.');
    setShowBookingForm(false);
  };

  const getNextAvailableDates = () => {
    return availableDates?.filter(d => d?.available)?.sort((a, b) => new Date(a.date) - new Date(b.date))?.slice(0, 6);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Réservation & Disponibilités</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Réservez {artist?.business_name || 'cet artiste'} pour votre prochain événement. 
          Consultez les disponibilités et choisissez la formule qui vous convient.
        </p>
      </div>
      {/* Quick Stats */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{artist?.total_bookings || 0}</div>
            <div className="text-sm text-gray-600">Événements réalisés</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-400 fill-current" />
              <span className="text-2xl font-bold text-purple-600 ml-1">
                {artist?.average_rating || 'Nouveau'}
              </span>
            </div>
            <div className="text-sm text-gray-600">Note moyenne</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">24h</div>
            <div className="text-sm text-gray-600">Temps de réponse</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">€3k-8k</div>
            <div className="text-sm text-gray-600">Gamme de prix</div>
          </div>
        </div>
      </div>
      {/* Pricing Packages */}
      <div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-6">Formules de prestation</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {pricingPackages?.map((pkg) => (
            <div
              key={pkg?.id}
              className={`relative bg-white rounded-xl shadow-sm border-2 p-6 hover:shadow-md transition-shadow ${
                selectedPackage === pkg?.id ? 'border-blue-500' : 'border-gray-200'
              } ${pkg?.popular ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}
            >
              {pkg?.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                    ⭐ Populaire
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">{pkg?.name}</h4>
                <div className="text-3xl font-bold text-blue-600 mb-1">€{pkg?.price?.toLocaleString()}</div>
                <div className="text-sm text-gray-500">{pkg?.duration}</div>
                <p className="text-gray-600 mt-2">{pkg?.description}</p>
              </div>

              <ul className="space-y-2 mb-6">
                {pkg?.features?.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="bg-gray-50 rounded-lg p-3 mb-6">
                <p className="text-sm text-gray-600">{pkg?.suitable}</p>
              </div>

              <button
                onClick={() => {
                  setSelectedPackage(pkg?.id);
                  setShowBookingForm(true);
                }}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  pkg?.popular
                    ? 'bg-blue-600 text-white hover:bg-blue-700' :'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Choisir cette formule
              </button>
            </div>
          ))}
        </div>
      </div>
      {/* Available Dates */}
      <div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-6">Prochaines disponibilités</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getNextAvailableDates()?.map((dateInfo) => (
            <button
              key={dateInfo?.date}
              onClick={() => setSelectedDate(dateInfo?.date)}
              className={`p-4 rounded-lg border-2 text-left hover:shadow-md transition-all ${
                selectedDate === dateInfo?.date
                  ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-lg font-semibold text-gray-900">
                  {new Date(dateInfo.date)?.toLocaleDateString('fr-FR', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
                <div className="text-green-600 font-medium">
                  À partir de €{dateInfo?.price?.toLocaleString()}
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {new Date(dateInfo.date)?.toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-semibold text-gray-900">Demande de réservation</h3>
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleBookingSubmit} className="space-y-6">
                {/* Selected Package & Date */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Sélection actuelle</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {selectedPackage && (
                      <div>
                        <span className="text-gray-600">Formule: </span>
                        <span className="font-medium">
                          {pricingPackages?.find(p => p?.id === selectedPackage)?.name}
                        </span>
                      </div>
                    )}
                    {selectedDate && (
                      <div>
                        <span className="text-gray-600">Date: </span>
                        <span className="font-medium">
                          {new Date(selectedDate)?.toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Event Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Type d'événement *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {eventTypes?.map((type) => (
                      <button
                        key={type?.id}
                        type="button"
                        onClick={() => setBookingForm(prev => ({ ...prev, eventType: type?.id }))}
                        className={`p-3 rounded-lg border text-left hover:border-blue-300 transition-colors ${
                          bookingForm?.eventType === type?.id
                            ? 'border-blue-500 bg-blue-50' :'border-gray-200'
                        }`}
                      >
                        <div className="text-lg mb-1">{type?.icon}</div>
                        <div className="text-sm font-medium text-gray-900">{type?.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Event Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre d'invités attendus *
                    </label>
                    <select
                      value={bookingForm?.expectedAttendees}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, expectedAttendees: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="1-50">1-50 personnes</option>
                      <option value="51-100">51-100 personnes</option>
                      <option value="101-300">101-300 personnes</option>
                      <option value="301-500">301-500 personnes</option>
                      <option value="500+">500+ personnes</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget approximatif *
                    </label>
                    <select
                      value={bookingForm?.budget}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, budget: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner</option>
                      <option value="2000-4000">€2,000 - €4,000</option>
                      <option value="4000-6000">€4,000 - €6,000</option>
                      <option value="6000-10000">€6,000 - €10,000</option>
                      <option value="10000+">€10,000+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lieu de l'événement *
                  </label>
                  <input
                    type="text"
                    value={bookingForm?.location}
                    onChange={(e) => setBookingForm(prev => ({ ...prev, location: e?.target?.value }))}
                    placeholder="Ville ou adresse de l'événement"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Informations complémentaires
                  </label>
                  <textarea
                    value={bookingForm?.additionalInfo}
                    onChange={(e) => setBookingForm(prev => ({ ...prev, additionalInfo: e?.target?.value }))}
                    placeholder="Détails sur votre événement, préférences musicales, contraintes techniques..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowBookingForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Envoyer la demande
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Contact Section */}
      <div className="bg-gray-50 rounded-xl p-6">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Une question sur la réservation ?</h3>
          <p className="text-gray-600 mb-4">
            Notre équipe est disponible pour vous accompagner dans votre projet
          </p>
          <button className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
            <MessageCircle className="w-5 h-5 mr-2" />
            Contacter l'artiste
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingAvailability;